/**
 * 附件 URL 工具：仅依据「去掉 query/hash 后的路径」上的扩展名做判断；
 * 与名称旁展示的后缀解析（如 {@link getTypebyUrl}）一致，均应忽略 query。
 */

/** 取 URL 中用于匹配扩展名的路径段（不含 ?、# 之后） */
function pathForAttachmentExtMatch(url: string): string {
  if (!url) return '';
  return url.split(/[?#]/)[0];
}

/**
 * 是否为音频资源链接。
 * 扩展名：mp3、wav、ogg、m4a、aac、flac、wma、ape、mka、opus、alac、pcm 及常见多声道封装 dts、ac3、eac3、truehd、dts-hd。
 */
export function isAudioUrl(url: string): boolean {
  return /\.(mp3|wav|ogg|m4a|aac|flac|wma|ape|mka|opus|alac|pcm|dts|ac3|eac3|truehd|dts-hd)$/i.test(
    pathForAttachmentExtMatch(url),
  );
}

/**
 * 是否为 PowerPoint / Keynote 演示文稿链接。
 * 扩展名：ppt、pptx、pptm、pps、ppsx、ppsm、key。
 */
export function isPPtUrl(url: string): boolean {
  return /\.(ppt|pptx|pptm|pps|ppsx|ppsm|key)$/i.test(pathForAttachmentExtMatch(url));
}

/**
 * 是否为 Excel / 表格类链接。
 * 扩展名：xlsx、xls、xlsm、xlsb、xltx、xltm、csv。
 */
export function isExcelUrl(url: string): boolean {
  return /\.(xlsx|xls|xlsm|xlsb|xltx|xltm|csv)$/i.test(pathForAttachmentExtMatch(url));
}

/**
 * 是否为 PDF 链接。
 * 扩展名：pdf。
 */
export function isPdfUrl(url: string): boolean {
  return /\.pdf$/i.test(pathForAttachmentExtMatch(url));
}

/**
 * 是否为 Markdown 文稿链接。
 * 扩展名：md、markdown、mdown。
 */
export function isMarkdownUrl(url: string): boolean {
  return /\.(md|markdown|mdown)$/i.test(pathForAttachmentExtMatch(url));
}

/**
 * 是否为 HTML 链接。
 * 扩展名：html、htm。
 */
export function isHtmlUrl(url: string): boolean {
  return /\.(html|htm)$/i.test(pathForAttachmentExtMatch(url));
}

/**
 * 是否为 Word / 字处理文稿链接。
 * 扩展名：doc、docx、docm、dot、dotx、dotm、wps、wpt、wpd、rtf。
 */
export function isWordUrl(url: string): boolean {
  return /\.(doc|docx|docm|dot|dotx|dotm|wps|wpt|wpd|rtf)$/i.test(pathForAttachmentExtMatch(url));
}

/**
 * 由 URL 解析得到的附件业务分类，供内联角标、列表图标等复用。
 */
export type MediaAttachmentFileKind =
  | 'audio'
  | 'word'
  | 'ppt'
  | 'excel'
  | 'pdf'
  | 'html'
  | 'markdown';

/**
 * 按扩展名解析附件类型；各类扩展名互斥，未命中返回 null（视为通用文件）。
 * 判断顺序不影响结果，仅便于阅读：音频 → 字处理 → 演示 → 表格 → PDF → Markdown。
 */
export function getMediaAttachmentFileKind(url: string): MediaAttachmentFileKind | null {
  if (!url) return null;
  if (isAudioUrl(url)) return 'audio';
  if (isWordUrl(url)) return 'word';
  if (isPPtUrl(url)) return 'ppt';
  if (isExcelUrl(url)) return 'excel';
  if (isPdfUrl(url)) return 'pdf';
  if (isMarkdownUrl(url)) return 'markdown';
  if (isHtmlUrl(url)) return 'html';
  return null;
}
